import type { DefinitionMode, DevelopmentPlan, PlanStepId } from './plans.types';

/** Mêmes noms et même ordre que le tableau des plans de l'accueil. */
export const PLAN_NAMES = [
  'Scénario de référence', 'Extension vers les écoles', 'Boucle du centre-ville',
  'Raccordement des logements', 'Variante géothermie', 'Desserte des équipements publics',
  'Extension Val de Bièvre', 'Variante biomasse',
];

export const PLAN_STEPS: { id: PlanStepId; label: string; validate: string; hint: string }[] = [
  { id: 'buildings', label: 'Bâtiments', validate: 'Valider les bâtiments', hint: 'Bâtiments du projet inclus dans ce plan.' },
  { id: 'route', label: 'Tracé du réseau', validate: 'Valider le tracé', hint: 'Tracé qui relie les bâtiments du plan.' },
  { id: 'phasing', label: 'Phasage', validate: 'Valider le phasage', hint: 'Ordre et période de raccordement des bâtiments.' },
];

export const MODE_LABELS: Record<DefinitionMode, string> = { manual: 'Manuel', algorithm: 'Algorithme' };

/** Hypothèses de démonstration, reprises du tableau de l'accueil. */
export const GWH_PER_BUILDING = 0.085;
export const KM_PER_BUILDING = 0.012;
export const DEMO_SHARE = 0.25;

const SEED_PROGRESS = [3, 2, 1, 3, 2, 1, 3, 2];

/** Plans de démonstration, alignés sur le nombre de plans du projet. */
export function seedPlans(projectId: string, count: number, updatedAt: string, projectBuildings: number): DevelopmentPlan[] {
  const [datePart, timePart = '09:00'] = updatedAt.split(' ');
  const [day, month, year] = datePart.split('/').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return Array.from({ length: count }, (_, i) => {
    const date = new Date(Date.UTC(year, month - 1, day - i * 2, hours, minutes));
    const buildings = Math.max(1, Math.round(projectBuildings * Math.min(0.95, DEMO_SHARE + i * 0.12)));
    const completedSteps = SEED_PROGRESS[i % SEED_PROGRESS.length];
    return {
      id: `${projectId}-plan-${i + 1}`,
      projectId,
      name: PLAN_NAMES[i % PLAN_NAMES.length],
      updatedAt: date.toLocaleString('fr-FR', { timeZone: 'UTC', dateStyle: 'short', timeStyle: 'short' }),
      completedSteps,
      routeMode: i % 2 === 0 ? 'algorithm' : 'manual',
      phasingMode: i % 3 === 0 ? 'algorithm' : 'manual',
      buildingCount: buildings,
      consumption: buildings * GWH_PER_BUILDING,
      networkLength: completedSteps >= 2 ? buildings * KM_PER_BUILDING : null,
    };
  });
}

export function progressLabel(plan: DevelopmentPlan): string {
  if (plan.completedSteps >= PLAN_STEPS.length) return 'Plan complet';
  return `Étape ${plan.completedSteps + 1}/3 · ${PLAN_STEPS[plan.completedSteps].label} à définir`;
}

export const formatMetric = (value: number) => value.toLocaleString('fr-FR', { maximumFractionDigits: 1 });

/** Dates métier françaises ; à égalité, l'API place la dernière mutation en tête. */
export function latestDevelopmentPlan(plans: DevelopmentPlan[]): DevelopmentPlan | undefined {
  const time = (plan: DevelopmentPlan) => {
    const [day, month, year, hours = 0, minutes = 0] = plan.updatedAt.match(/\d+/g)!.map(Number);
    return Date.UTC(year, month - 1, day, hours, minutes);
  };
  return plans.reduce<DevelopmentPlan | undefined>((latest, plan) =>
    !latest || time(plan) > time(latest) ? plan : latest, undefined);
}
